/**
 * What the TallyPrime screens say: the state of a connection, how old its
 * figures are, and what to do when something is wrong. In plain words — no
 * XML, no ports, no terminals on the happy path; the networking detail lives
 * under Troubleshooting and Security.
 *
 * Never "live": a figure is "updated from TallyPrime 3 minutes ago", and a
 * connector that has gone quiet is offline, whatever its last figures were.
 */
import { OFFLINE_AFTER_FAILING_MINUTES, OFFLINE_AFTER_MINUTES, STALE_AFTER_MINUTES, sourceState, type DateFormatter, type SourceState } from '@timely/domain';
import type { ConnectorStatus } from '../useConnector';

type Tone = 'pos' | 'brand' | 'warn' | 'dang' | 'neutral';

export const STATE: Record<SourceState, { label: string; tone: Tone }> = {
    connected: { label: 'Connected', tone: 'pos' },
    connecting: { label: 'Connecting', tone: 'brand' },
    offline: { label: 'Offline', tone: 'warn' },
    stale: { label: 'Stale', tone: 'warn' },
    needs_attention: { label: 'Needs attention', tone: 'dang' },
    disconnected: { label: 'Disconnected', tone: 'neutral' },
    not_configured: { label: 'Not set up', tone: 'neutral' },
};

/** Whether the connector's last report, or the server's last attempt, carried a problem: it then backs off between check-ins. */
export function reportedProblem(status: ConnectorStatus | null): boolean {
    const report = status?.lastReport;
    return report?.tallyReachable === false || report?.companyLoaded === false
        || Boolean(report?.lastError?.trim()) || Boolean(status?.lastError);
}

/**
 * The connection's state, as the domain decides it: offline after 20 minutes' silence (35 while the
 * connector is backing off after a problem, as the freshness on every screen counts it), stale after
 * 6 hours without a sync.
 */
export function connectionState(status: ConnectorStatus | null, now: Date): SourceState {
    return sourceState({
        configured: status !== null, status: status?.status ?? null,
        lastSeenAt: status?.lastSeenAt ?? null, lastSuccessAt: status?.lastSuccessAt ?? null, lastError: status?.lastError ?? null, now,
        reportedProblem: reportedProblem(status),
    }).state;
}

/** "just now", "3 minutes ago", "2 hours ago", "on 01 Oct 2026". */
export function ago(iso: string | null | undefined, now: Date, date: DateFormatter): string {
    if (!iso) return 'never';
    const minutes = Math.floor((now.getTime() - new Date(iso).getTime()) / 60_000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    return `on ${date.long(iso)}`;
}

/** One line under the badge: how current the figures are, in words. */
export function freshnessLine(state: SourceState, status: ConnectorStatus, now: Date, date: DateFormatter): string {
    // "the last update was 2 hours ago", "… was on 08 Oct 2026" (not "the figures are from on 08 Oct 2026").
    const updated = status.lastSuccessAt ? `the last update was ${ago(status.lastSuccessAt, now, date)}` : 'nothing has been read yet';
    switch (state) {
        case 'connected': return `Updated from TallyPrime ${ago(status.lastSuccessAt, now, date)}`;
        case 'connecting': return status.status === 'pending' ? 'Being set up' : 'Waiting for the first update from TallyPrime';
        case 'offline': return `Timely Connector has not checked in since ${ago(status.lastSeenAt, now, date)} (more than ${reportedProblem(status) ? OFFLINE_AFTER_FAILING_MINUTES : OFFLINE_AFTER_MINUTES} minutes); ${updated}`;
        case 'stale': return `Timely Connector is checking in, but there has been no successful update for more than ${STALE_AFTER_MINUTES / 60} hours; ${updated}`;
        case 'needs_attention': return `The last update did not go through; ${updated}`;
        case 'disconnected': return status.revokedAt ? `Disconnected ${ago(status.revokedAt, now, date)}` : 'Disconnected';
        case 'not_configured': return 'Not set up';
    }
}

export interface Trouble { id: string; title: string; body: string }

/** What to do, for each thing that goes wrong. Shown under Troubleshooting, and linked from a problem when it happens. */
export const TROUBLESHOOTING: readonly Trouble[] = [
    { id: 'not-running', title: 'TallyPrime is not running',
      body: 'Open TallyPrime on the computer where Timely Connector is installed. Timely reads TallyPrime only while it is open, and catches up as soon as it is.' },
    { id: 'connection-off', title: 'TallyPrime is open but cannot be reached',
      body: 'In TallyPrime, open F1 Help → Settings → Connectivity → Client/Server configuration, and set "TallyPrime acts as" to Both (or Server), port 9000. Then restart TallyPrime.' },
    { id: 'no-company', title: 'No company is loaded in TallyPrime',
      body: 'Open the company you want to connect in TallyPrime. Timely only sees the companies that are open there.' },
    { id: 'wrong-company', title: 'The wrong company is connected',
      body: 'Disconnect TallyPrime here and connect again, choosing the right company. Timely reads only the company chosen here, even when a different one is open on screen in TallyPrime.' },
    { id: 'offline', title: 'Timely Connector is offline',
      body: 'Check that the computer with TallyPrime is switched on, connected to the internet, and that Timely Connector is running. Your figures stay as they were last updated; nothing is lost.' },
    { id: 'revoked', title: 'The connector was disconnected',
      body: 'A disconnected connector cannot send anything again. Connect TallyPrime again from this page to make a new pairing code; accounts from the same TallyPrime company are recognised automatically.' },
    { id: 'too-large', title: 'The company is too large to send',
      body: 'This version can send the receivables of about 7,500 customers with ten open bills each, and more when they have fewer. If yours is larger, contact Timely support; nothing has been changed.' },
    { id: 'review', title: 'An update is waiting for your review',
      body: 'When an update would mark many accounts as paid at once, Timely holds it and shows you the accounts first. If those customers really have paid, approve it; if not, check that the right company is open in TallyPrime.' },
];

/** The security note: in Troubleshooting & security, not on the happy path. */
export const SECURITY_NOTE = 'Timely Connector reads TallyPrime on the same computer, and sends your receivables to Timely Payment over an encrypted connection that it opens itself. '
    + 'Timely never needs TallyPrime to be reachable from other computers or from the internet: no port forwarding, no public IP address, no VPN. '
    + 'Timely never changes anything in TallyPrime.';

export const NETWORK_EXPOSED = 'TallyPrime on this computer is accepting connections from other computers on your network. That is TallyPrime\'s own setting, not something Timely needs. '
    + 'If nobody uses TallyPrime from another computer, ask whoever looks after your computers to block port 9000 for incoming connections in Windows Firewall. Timely will keep working.';
